<template>
  <div class="w-full">
    <EmptyScreen
      v-if="!issueCategories || issueCategories.length === 0"
      source="multiple_issues"
      text="No issues found"
    />

    <div v-else class="flex flex-col gap-2">
      <a-collapse
        v-model:activeKey="activeKeys"
        :bordered="false"
        expand-icon-position="end"
        class="borderless-collapse"
      >
        <a-collapse-panel
          v-for="(issue, index) in mappedIssues"
          :key="`issue_${index}`"
          class="borderless-panel"
        >
          <template #header>
            <span class="text-sm font-medium text_color">
              {{ issue.title }}
            </span>
          </template>

          <div class="text-sm">
            <CustomTable
              v-if="issue.tableData && issue.tableData.length > 0"
              :headers="issue.headers"
              :data="issue.tableData"
              :pagination="null"
              :show-page-size="false"
              :source="`multiple_issues_${issue.categoryId}`"
              height="300px"
              :custom-column-render="issue.customColumnRender"
              @sequence-selected="handleSequenceSelection"
            />
            <div v-else class="text-xs secondary_color py-4 text-center">
              No data available for this category
            </div>
          </div>
        </a-collapse-panel>
      </a-collapse>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import CustomTable from "@/src/components/custom_table_components/CustomTable.vue";
import SequenceDisplayCell from "@/src/components/sequences_components/SequenceDisplayCell.vue";
import EmptyFieldDisplay from "@/src/components/EmptyFieldDisplay.vue";
import {
  getValidationTableHeaders,
  getValidationCustomColumnRender,
} from "@/src/utils/sequenceUtils.js";
import {
  validationCategories,
  MULTIPLE_ISSUES_CATEGORY_KEYS,
  VALIDATION_CATEGORY_KEYS,
  CATEGORY_ID_TO_KEY_MAPPING,
} from "@/src/constants/sequenceConstants.js";
import EmptyScreen from "@/src/components/EmptyScreen.vue";

const props = defineProps({
  issueCategories: {
    type: Array,
    default: () => [],
  },
  entityData: {
    type: Object,
    default: () => ({}),
  },
  currentPerson: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(["sequence-selected"]);

const activeKeys = ref([]);

const generateCategoryData = (categoryKey, currentPerson) => {
  const basePersonData = {
    id: currentPerson.id,
    first_name: currentPerson.first_name,
    last_name: currentPerson.last_name,
    email: currentPerson.email,
    linkedin_url: currentPerson.linkedin_url,
    entity_type: currentPerson.entity_type,
    profile_pic: currentPerson.profile_pic,
    current_role: currentPerson.current_role,
    current_company: currentPerson.company_name,
    company_logo: currentPerson.logo_url,
  };

  switch (categoryKey) {
    case VALIDATION_CATEGORY_KEYS.EMPTY_LINKEDIN:
      return [{ ...basePersonData }];

    case VALIDATION_CATEGORY_KEYS.EMPTY_EMAIL:
      return [{ ...basePersonData }];

    case VALIDATION_CATEGORY_KEYS.COMPLETED_SEQUENCES:
    case VALIDATION_CATEGORY_KEYS.ONGOING_SEQUENCES:
      return [
        {
          ...basePersonData,
          sequence_data: currentPerson.sequence_data || [],
          ongoing_sequences: currentPerson.ongoing_sequences || [],
          finished_sequences: currentPerson.finished_sequences || [],
          current_sequence: currentPerson.current_sequence,
        },
      ];

    default:
      return [{ ...basePersonData }];
  }
};

const formatEntitiesForTable = (entities) => {
  return (
    entities?.map((entity) => {
      const fullName =
        `${entity.first_name || ""} ${entity.last_name || ""}`.trim();
      const displayName = fullName || entity.email || "";

      return {
        ...entity,
        name: fullName,
        display_name: displayName,
      };
    }) || []
  );
};

const getCustomColumnRender = (categoryKey) => {
  return getValidationCustomColumnRender(categoryKey, {
    EmptyFieldDisplay,
    SequenceDisplayCell,
    MultipleIssuesRenderer: null,
    onSequenceSelected: handleSequenceSelection,
    validationResults: props.entityData,
    currentPerson: props.currentPerson,
  });
};

const getFilteredHeaders = (category) => {
  const headers = getValidationTableHeaders(category);

  if (MULTIPLE_ISSUES_CATEGORY_KEYS.includes(category.key)) {
    return headers.filter((header) => header.key !== "display_name");
  }

  return headers;
};

const mappedIssues = computed(() => {
  if (!props.issueCategories || !Array.isArray(props.issueCategories)) {
    return [];
  }

  return props.issueCategories
    .map((categoryId) => {
      const categoryKey = CATEGORY_ID_TO_KEY_MAPPING[categoryId];
      if (!categoryKey) return null;

      const category = validationCategories[categoryKey];
      if (!category) return null;

      const categoryData = generateCategoryData(
        categoryKey,
        props.currentPerson,
      );
      const formattedData = formatEntitiesForTable(categoryData);

      return {
        categoryId,
        title: category.title,
        categoryKey,
        headers: getFilteredHeaders(category),
        tableData: formattedData,
        customColumnRender: getCustomColumnRender(categoryKey),
      };
    })
    .filter(Boolean);
});

const handleSequenceSelection = (updatedRow) => {
  emit("sequence-selected", updatedRow);
};
</script>

<style scoped>
:deep(.borderless-collapse) {
  background: transparent !important;
  border: none !important;
}

:deep(.borderless-collapse .ant-collapse-item) {
  border: none !important;
  background: transparent !important;
  margin-bottom: 4px !important;
}

:deep(.borderless-collapse .ant-collapse-header) {
  border: none !important;
  background: transparent !important;
  padding: 8px 12px !important;
  border-radius: 6px !important;
  transition: background-color 0.2s ease !important;
}

:deep(.borderless-collapse .ant-collapse-content) {
  border: none !important;
  background: transparent !important;
}

:deep(.borderless-collapse .ant-collapse-content-box) {
  padding: 0 !important;
  border-radius: 6px !important;
  margin-top: 4px !important;
}
</style>
